/**
 * IntentML API (ISSUE-204–208) — validation, preview, apply jobs.
 * ISSUE-215 — company write-back ``POST /intentml/import``.
 * Backend: Krysalis_Backend ``/api/v1/intentml/*``.
 */

import axios from 'axios';
import { apiClient } from '../apiClient';
import { broadcastIntentmlImportComplete } from '../intentmlSnapshotBroadcast';
import { fetchIcpDefinitionItems } from './icpDefinitionsService';

export type IntentMLSemanticDepth = 'api' | 'full';

export interface IntentMLDiagnostic {
  code: string;
  message: string;
  severity: 'error' | 'warning' | 'info';
  path: string;
  section: string;
  hint?: string;
}

export interface IntentMLValidationResult {
  valid: boolean;
  diagnostics: IntentMLDiagnostic[];
  diagnostics_by_section: Record<string, IntentMLDiagnostic[]>;
}

export interface IntentMLPreviewOptions {
  strict?: boolean;
  target_scope?: string | null;
  allow_deprecations?: boolean;
}

export interface IntentMLPreviewResult {
  summary: Record<string, number>;
  changes: Array<{ entity_type: string; count: number; items: unknown[] }>;
  blocked: IntentMLDiagnostic[];
  policy_violations: IntentMLDiagnostic[];
  requires_approval: boolean;
  preview_hash: string;
  validation: IntentMLValidationResult;
}

export interface IntentMLApplyRequestBody {
  document: Record<string, unknown>;
  preview_hash?: string | null;
  idempotency_key: string;
  force?: boolean;
  merge_strategy?: string | null;
}

export interface IntentMLApplyResponseBody {
  job_id: string;
  status: 'queued' | 'running' | 'requires_approval' | 'completed' | 'failed';
  message: string;
}

export interface IntentMLJobStep {
  name: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  errors: string[];
  detail?: string | null;
}

export interface IntentMLJobStatus {
  job_id: string;
  status: 'queued' | 'running' | 'requires_approval' | 'completed' | 'failed';
  started_at?: string | null;
  completed_at?: string | null;
  current_step?: string | null;
  steps: IntentMLJobStep[];
  result_summary?: Record<string, unknown> | null;
  error_message?: string | null;
  payload_hash?: string | null;
  preview_hash?: string | null;
}

/** POST /intentml/import (ISSUE-215) */
export interface IntentMLCompanyImportResponse {
  import_id: string;
  artifacts_updated: number;
  artifact_items_updated: number;
  metrics_recalculated: boolean;
}

export interface IntentMLImportErrorItem {
  code: string;
  path: string;
  message: string;
}

export class IntentMLImportValidationError extends Error {
  readonly errors: IntentMLImportErrorItem[];

  constructor(errors: IntentMLImportErrorItem[]) {
    super(errors.map((e) => e.message).join('; ') || 'IntentML import validation failed');
    this.name = 'IntentMLImportValidationError';
    this.errors = errors;
  }
}

export class IntentMLService {
  private static readonly BASE = '/intentml';

  static async validatePrime(
    document: Record<string, unknown>,
    semanticDepth: IntentMLSemanticDepth = 'api'
  ): Promise<IntentMLValidationResult> {
    const q = new URLSearchParams({ semantic_depth: semanticDepth });
    const { data } = await apiClient.post<IntentMLValidationResult>(
      `${this.BASE}/prime/validate?${q.toString()}`,
      document
    );
    return data;
  }

  static async previewPrime(
    document: Record<string, unknown>,
    options: IntentMLPreviewOptions = {},
    semanticDepth: IntentMLSemanticDepth = 'full'
  ): Promise<IntentMLPreviewResult> {
    const q = new URLSearchParams({ semantic_depth: semanticDepth });
    const { data } = await apiClient.post<IntentMLPreviewResult>(
      `${this.BASE}/prime/preview?${q.toString()}`,
      { document, options }
    );
    return data;
  }

  static async applyPrime(body: IntentMLApplyRequestBody): Promise<IntentMLApplyResponseBody> {
    const { data } = await apiClient.post<IntentMLApplyResponseBody>(
      `${this.BASE}/prime/apply`,
      body
    );
    return data;
  }

  static async getJob(jobId: string): Promise<IntentMLJobStatus> {
    const { data } = await apiClient.get<IntentMLJobStatus>(`${this.BASE}/jobs/${jobId}`);
    return data;
  }

  /**
   * ISSUE-215 — transactional company IntentML import (`document_type`: business_model | company_model).
   * On **422**, throws ``IntentMLImportValidationError`` with ``errors[]``.
   * ISSUE-220 — when ``companyIdForBroadcast`` is set, notifies other tabs to refresh snapshot data.
   */
  static async importCompanyDocument(
    document: Record<string, unknown>,
    companyIdForBroadcast?: string
  ): Promise<IntentMLCompanyImportResponse> {
    try {
      const { data } = await apiClient.post<IntentMLCompanyImportResponse>(
        `${this.BASE}/import`,
        document
      );
      if (companyIdForBroadcast?.trim()) {
        const cid = companyIdForBroadcast.trim();
        let icpDefinitionItemCount: number | undefined;
        try {
          const icp = await fetchIcpDefinitionItems(cid);
          icpDefinitionItemCount = icp.count;
        } catch (probeErr: unknown) {
          console.warn('[IntentML] ICP definition items probe failed after import (non-fatal)', probeErr);
        }
        broadcastIntentmlImportComplete(cid, { icpDefinitionItemCount });
      }
      return data;
    } catch (e: unknown) {
      if (axios.isAxiosError(e) && e.response?.status === 422) {
        const body = e.response.data as { errors?: IntentMLImportErrorItem[] };
        if (body?.errors?.length) {
          throw new IntentMLImportValidationError(body.errors);
        }
      }
      throw e;
    }
  }

  /** Poll until job reaches a terminal status or timeout. */
  static async pollJobUntilTerminal(
    jobId: string,
    options?: { intervalMs?: number; maxMs?: number }
  ): Promise<IntentMLJobStatus> {
    const intervalMs = options?.intervalMs ?? 500;
    const maxMs = options?.maxMs ?? 120_000;
    const start = Date.now();
    while (Date.now() - start < maxMs) {
      const j = await this.getJob(jobId);
      if (j.status === 'completed' || j.status === 'failed') {
        return j;
      }
      await new Promise((r) => setTimeout(r, intervalMs));
    }
    throw new Error('IntentML job polling timed out');
  }
}
